import React, { useState } from 'react';
import { 
  Box, 
  Tabs, 
  Tab
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { 
  FileText, 
  Table
} from 'lucide-react';
import DocumentEditor from './DocumentEditor.tsx';
import SpreadsheetEditor from './SpreadsheetEditor.tsx';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel = (props: TabPanelProps) => {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`document-tools-tabpanel-${index}`}
      aria-labelledby={`document-tools-tab-${index}`}
      {...other}
      //style={{ height: 'calc(100% - 49px)' }}
    >
      {value === index && (
        <Box sx={{ height: '100%', pt: 2 }}>
          {children}
        </Box>
      )}
    </div>
  );
};

const StyledTabs = styled(Tabs)(({ theme }) => ({
  borderBottom: `1px solid rgba(138, 43, 226, 0.2)`,
  '& .MuiTabs-indicator': {
    backgroundColor: '#00FFFF',
    height: 3,
  },
}));

const StyledTab = styled(Tab)(({ theme }) => ({
  textTransform: 'none',
  minWidth: 72,
  fontWeight: 500,
  marginRight: theme.spacing(2),
  color: 'rgba(255, 255, 255, 0.7)',
  '&.Mui-selected': {
    color: '#00FFFF',
  },
  '&.Mui-focusVisible': {
    backgroundColor: 'rgba(138, 43, 226, 0.1)',
  },
}));

interface DocumentToolsProps {
  initialTab?: number;
  initialDocumentContent?: string;
  initialSpreadsheetData?: readonly any[];
  initialSpreadsheetColumns?: any[];
  onSaveDocument?: (content: string) => void;
  onSaveSpreadsheet?: (rows: readonly any[], columns: any[]) => void;
  onAddDocumentToProposal?: (content: string, cost: number) => void;
  onAddSpreadsheetToProposal?: (rows: readonly any[], columns: any[], cost: number) => void;
  problemText?: string;
  setProblemText?: (text: string) => void;
  onClose?: () => void;
}

const DocumentTools: React.FC<DocumentToolsProps> = ({
  initialTab = 0,
  initialDocumentContent = '',
  initialSpreadsheetData = [],
  initialSpreadsheetColumns = [],
  onSaveDocument,
  onSaveSpreadsheet,
  onAddDocumentToProposal,
  onAddSpreadsheetToProposal,
  problemText,
  setProblemText,
  onClose
}) => {
  const [value, setValue] = useState(initialTab);

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  return (
    <Box sx={{ width: '100%', height: '100%' }}>
      <StyledTabs value={value} onChange={handleChange} aria-label="document tools tabs">
        <StyledTab icon={<FileText size={16} />} label="Document" />
        <StyledTab icon={<Table size={16} />} label="Spreadsheet" />
      </StyledTabs>
      
      <TabPanel value={value} index={0}>
        <DocumentEditor 
          initialContent={initialDocumentContent}
          onSave={onSaveDocument}
          onAddToProposal={onAddDocumentToProposal}
          problemText={problemText}
          setProblemText={setProblemText}
        />
      </TabPanel>
      <TabPanel value={value} index={1}>
        <SpreadsheetEditor 
          initialData={initialSpreadsheetData as any}
          initialColumns={initialSpreadsheetColumns}
          onSave={onSaveSpreadsheet as any}
          onAddToProposal={onAddSpreadsheetToProposal as any}
          problemText={problemText}
          setProblemText={setProblemText}
        />
      </TabPanel>
    </Box>
  );
};

export default DocumentTools; 